import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Lock, KeyRound, ShieldCheck, Fingerprint, ScrollText, RotateCcw,
  Server, Activity, FileCheck, Bell, ArrowRight, Mail,
} from "lucide-react";
import { GlassNav, FadeUp, StaggerChildren, StaggerItem, HoverLift } from "@/components/motion";
import { salesInquiryHref, STATUS_PAGE_URL, SECURITY_EMAIL, SECURITY_MAILTO } from "@/lib/support";

const TRUST_BADGES = [
  "Encryption in transit and at rest",
  "Per-organization isolation",
  "GDPR-aligned controls",
];

const DATA_PROTECTION = [
  {
    icon: Lock,
    title: "Encryption in transit",
    desc: "TLS 1.2+ on all traffic between browsers, our API, and sub-processors.",
  },
  {
    icon: Server,
    title: "Encryption at rest",
    desc: "Customer data is stored in managed Postgres and object storage with provider-managed encryption at rest (AES-256 class).",
  },
  {
    icon: KeyRound,
    title: "Isolated tenancy",
    desc: "Every query is scoped to the signed-in organization. Cross-tenant access is rejected — not masked.",
  },
];

const IDENTITY_ACCESS = [
  {
    icon: Fingerprint,
    title: "Authentication via Clerk",
    desc: "Email/password today; enterprise SSO (Okta, Entra ID, Google Workspace) available when configured for your campus.",
  },
  {
    icon: ShieldCheck,
    title: "Role-based access",
    desc: "Admin and viewer roles plus department personas (legal, finance, HR, AD) so people only see what their job requires.",
  },
  {
    icon: ScrollText,
    title: "Audit trail",
    desc: "Material contract and owner-admin actions are logged with actor, timestamp, and context for review and export.",
  },
];

const RELIABILITY = [
  {
    icon: RotateCcw,
    title: "Backups & recovery",
    desc: "Automated daily database backups with point-in-time restore on the managed Postgres tier. Target RPO ≤ 24h; RTO ≤ 2h.",
  },
  {
    icon: Server,
    title: "Hardened application layer",
    desc: "Security headers (CSP, HSTS), CORS allowlists, rate limits, and malware scanning on uploads where enabled.",
  },
  {
    icon: Activity,
    title: "Monitoring",
    desc: "Application errors and performance monitored via Sentry, with owner-admin health surfaces for operators.",
  },
];

const COMPLIANCE = [
  {
    icon: FileCheck,
    title: "Security documentation",
    desc: "Tenant isolation, encryption in transit and at rest, audit logging, and a written security overview for campus IT and procurement.",
  },
  {
    icon: ShieldCheck,
    title: "GDPR-aligned data handling",
    desc: "DPA available on request. Subprocessors are listed at /subprocessors.",
  },
  {
    icon: Bell,
    title: "Incident response",
    desc: "In the event of a confirmed security incident affecting customer data, we notify affected customers within 72 hours.",
  },
];

const SUBPROCESSORS = [
  { name: "Replit", purpose: "Application hosting and managed Postgres (primary database + backups)" },
  { name: "Google Cloud Storage", purpose: "Private contract PDFs, clause images, and attachments" },
  { name: "Clerk", purpose: "Authentication, sessions, and optional enterprise SSO" },
  { name: "Stripe", purpose: "Billing and payment processing" },
  { name: "Resend", purpose: "Transactional email" },
  { name: "OpenAI", purpose: "Optional PDF → draft extraction (only when you trigger import)" },
  { name: "Sentry", purpose: "Application error and performance monitoring" },
];

export default function SecurityPage() {
  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">
      {/* GLASSY STICKY NAV — same as landing */}
      <GlassNav className="sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2.5">
            <img src="/logo.svg" alt="Zindeal" className="w-8 h-8 rounded-full shadow-sm" />
            <div className="flex flex-col leading-none">
              <span className="font-serif font-semibold text-lg tracking-tight text-[hsl(220,65%,12%)]">Zindeal</span>
              <span className="text-[8px] font-semibold uppercase tracking-[0.14em] text-[hsl(0,0%,32%)]">Workforce Intelligence for College Athletics</span>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-7 text-sm text-gray-500 font-medium">
            <a href="/#workflow" className="hover:text-gray-900 transition-colors">Coach lifecycle</a>
            <a href="/#faq" className="hover:text-gray-900 transition-colors">Questions</a>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link href="/sign-in">Sign in</Link>
            </Button>
            <Button asChild size="sm" className="bg-[hsl(220,55%,18%)] hover:bg-[hsl(220,65%,9%)] text-white shadow-sm">
              <a href={salesInquiryHref("Zindeal enterprise inquiry")}>Talk to sales</a>
            </Button>
          </div>
        </div>
      </GlassNav>

      {/* HERO — dark navy, matches landing hero treatment */}
      <section className="relative pt-24 pb-24 px-6 overflow-hidden bg-[hsl(220,65%,12%)]">
        <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
          <div
            className="absolute inset-0 opacity-60"
            style={{ background: "radial-gradient(ellipse 80% 60% at 50% -10%, hsl(220,55%,22%) 0%, transparent 70%)" }}
          />
          <div
            className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full"
            style={{ background: "radial-gradient(circle, hsl(42,90%,50%) 0%, transparent 70%)", filter: "blur(80px)", opacity: 0.5 }}
          />
          <div
            className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full"
            style={{ background: "radial-gradient(circle, hsl(220,60%,55%) 0%, transparent 70%)", filter: "blur(60px)" }}
          />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: "linear-gradient(hsl(0,0%,100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0,0%,100%) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />
        </div>

        <div className="max-w-4xl mx-auto text-center relative">
          <FadeUp delay={0}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/8 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/70 mb-8">
              <ShieldCheck className="h-3.5 w-3.5 text-[hsl(42,90%,50%)]" />
              Security &amp; Trust
            </div>
          </FadeUp>

          <FadeUp delay={0.1}>
            <h1
              className="text-4xl md:text-6xl font-extrabold leading-[1.05] text-white"
              style={{ letterSpacing: "-0.035em" }}
            >
              Security by design, not by afterthought.
            </h1>
          </FadeUp>

          <FadeUp delay={0.2}>
            <p className="mt-7 text-lg md:text-xl text-white/65 max-w-2xl mx-auto leading-relaxed">
              Zindeal handles sensitive coach contracts and compensation data. Here's how we protect it — plainly stated, no jargon shield.
            </p>
          </FadeUp>

          <FadeUp delay={0.3}>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-2.5">
              {TRUST_BADGES.map((b) => (
                <span
                  key={b}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.06] text-xs font-medium text-white/70"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[hsl(158,64%,48%)]" />
                  {b}
                </span>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* DATA PROTECTION — light */}
      <section className="py-24 px-6 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto">
          <FadeUp>
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <p className="text-[10px] text-gray-400 uppercase tracking-[0.18em] font-semibold mb-3">Data protection</p>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[hsl(220,65%,12%)]" style={{ letterSpacing: "-0.025em" }}>
                Your data, encrypted in transit and at rest.
              </h2>
            </div>
          </FadeUp>
          <StaggerChildren className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {DATA_PROTECTION.map((c) => (
              <StaggerItem key={c.title}>
                <HoverLift className="h-full">
                  <div className="bg-white border border-gray-100 rounded-2xl p-7 h-full flex flex-col" style={{ boxShadow: "var(--shadow-card)" }}>
                    <div className="w-11 h-11 rounded-xl bg-[hsl(220,55%,18%)] flex items-center justify-center mb-4">
                      <c.icon className="h-5 w-5 text-[hsl(42,90%,55%)]" />
                    </div>
                    <h3 className="font-bold text-lg mb-2 text-[hsl(220,65%,12%)]" style={{ letterSpacing: "-0.015em" }}>{c.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{c.desc}</p>
                  </div>
                </HoverLift>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* IDENTITY & ACCESS — dark navy + dot-grid pattern accent */}
      <section className="py-24 px-6 bg-[hsl(220,65%,7%)] relative overflow-hidden border-y border-white/8">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle, hsl(42,90%,55%) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
            maskImage: "radial-gradient(ellipse 100% 80% at 50% 50%, black 30%, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse 100% 80% at 50% 50%, black 30%, transparent 75%)",
            opacity: 0.08,
          }}
        />
        <div
          className="absolute inset-x-0 top-0 h-64 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 50% 100% at 50% 0%, hsl(42,90%,50%)/0.10 0%, transparent 70%)" }}
        />
        <div className="max-w-6xl mx-auto relative">
          <FadeUp>
            <div className="text-center mb-12 max-w-3xl mx-auto">
              <p className="text-[10px] text-white/40 uppercase tracking-[0.18em] font-semibold mb-3">Identity &amp; access</p>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white" style={{ letterSpacing: "-0.025em" }}>
                Strong identity, roles, and an audit trail you can export.
              </h2>
            </div>
          </FadeUp>
          <StaggerChildren className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {IDENTITY_ACCESS.map((c) => (
              <StaggerItem key={c.title}>
                <HoverLift className="h-full">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-7 h-full flex flex-col backdrop-blur-sm" style={{ boxShadow: "0 20px 40px -20px rgba(0,0,0,0.5)" }}>
                    <div className="w-11 h-11 rounded-xl bg-[hsl(42,90%,50%)]/15 border border-[hsl(42,90%,50%)]/25 flex items-center justify-center mb-4">
                      <c.icon className="h-5 w-5 text-[hsl(42,90%,55%)]" />
                    </div>
                    <h3 className="font-bold text-lg mb-2 text-white" style={{ letterSpacing: "-0.015em" }}>{c.title}</h3>
                    <p className="text-white/60 text-sm leading-relaxed">{c.desc}</p>
                  </div>
                </HoverLift>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* RELIABILITY & RECOVERY — light */}
      <section className="py-24 px-6 bg-[hsl(220,20%,97%)] border-b border-gray-100">
        <div className="max-w-6xl mx-auto">
          <FadeUp>
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <p className="text-[10px] text-gray-400 uppercase tracking-[0.18em] font-semibold mb-3">Reliability &amp; recovery</p>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[hsl(220,65%,12%)]" style={{ letterSpacing: "-0.025em" }}>
                Built to recover, not just to run.
              </h2>
            </div>
          </FadeUp>
          <StaggerChildren className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {RELIABILITY.map((c) => (
              <StaggerItem key={c.title}>
                <HoverLift className="h-full">
                  <div className="bg-white border border-gray-100 rounded-2xl p-7 h-full flex flex-col" style={{ boxShadow: "var(--shadow-card)" }}>
                    <div className="w-11 h-11 rounded-xl bg-[hsl(158,64%,38%)]/12 border border-[hsl(158,64%,38%)]/25 flex items-center justify-center mb-4">
                      <c.icon className="h-5 w-5 text-[hsl(158,64%,38%)]" />
                    </div>
                    <h3 className="font-bold text-lg mb-2 text-[hsl(220,65%,12%)]" style={{ letterSpacing: "-0.015em" }}>{c.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{c.desc}</p>
                  </div>
                </HoverLift>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* COMPLIANCE & PROGRAM — dark */}
      <section className="py-24 px-6 bg-[hsl(220,65%,12%)] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-15"
            style={{ background: "radial-gradient(circle, hsl(42,90%,50%) 0%, transparent 70%)", filter: "blur(80px)" }}
          />
        </div>
        <div className="max-w-6xl mx-auto relative">
          <FadeUp>
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <p className="text-[10px] text-white/40 uppercase tracking-[0.18em] font-semibold mb-3">Compliance &amp; program</p>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white" style={{ letterSpacing: "-0.025em" }}>
                Compliance and program maturity.
              </h2>
            </div>
          </FadeUp>
          <StaggerChildren className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {COMPLIANCE.map((c) => (
              <StaggerItem key={c.title}>
                <HoverLift className="h-full">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-7 h-full flex flex-col backdrop-blur-sm" style={{ boxShadow: "0 20px 40px -20px rgba(0,0,0,0.5)" }}>
                    <div className="w-11 h-11 rounded-xl bg-[hsl(42,90%,50%)]/15 border border-[hsl(42,90%,50%)]/25 flex items-center justify-center mb-4">
                      <c.icon className="h-5 w-5 text-[hsl(42,90%,55%)]" />
                    </div>
                    <h3 className="font-bold text-lg mb-2 text-white" style={{ letterSpacing: "-0.015em" }}>{c.title}</h3>
                    <p className="text-white/60 text-sm leading-relaxed">{c.desc}</p>
                  </div>
                </HoverLift>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* SUB-PROCESSORS — light */}
      <section className="py-24 px-6 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto">
          <FadeUp>
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <p className="text-[10px] text-gray-400 uppercase tracking-[0.18em] font-semibold mb-3">Sub-processors</p>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[hsl(220,65%,12%)]" style={{ letterSpacing: "-0.025em" }}>
                Who touches your data.
              </h2>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="rounded-2xl border border-gray-100 bg-white divide-y divide-gray-100 overflow-hidden" style={{ boxShadow: "var(--shadow-card)" }}>
              {SUBPROCESSORS.map((s) => (
                <div key={s.name} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 px-6 py-4">
                  <div className="sm:w-40 font-bold text-sm text-[hsl(220,65%,12%)]">{s.name}</div>
                  <div className="flex-1 text-sm text-gray-500">{s.purpose}</div>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* RESPONSIBLE DISCLOSURE / CONTACT — dark */}
      <section className="py-24 px-6 bg-[hsl(220,65%,12%)] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-0 left-1/4 w-80 h-80 rounded-full opacity-15"
            style={{ background: "radial-gradient(circle, hsl(42,90%,50%) 0%, transparent 70%)", filter: "blur(60px)" }}
          />
          <div
            className="absolute bottom-0 right-1/4 w-64 h-64 rounded-full opacity-10"
            style={{ background: "radial-gradient(circle, hsl(220,60%,55%) 0%, transparent 70%)", filter: "blur(60px)" }}
          />
        </div>
        <FadeUp>
          <div className="max-w-3xl mx-auto text-center relative">
            <div className="w-14 h-14 rounded-2xl bg-[hsl(42,90%,50%)]/15 border border-[hsl(42,90%,50%)]/25 flex items-center justify-center mx-auto mb-6">
              <Mail className="h-7 w-7 text-[hsl(42,90%,50%)]" />
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white" style={{ letterSpacing: "-0.025em" }}>
              Report a security concern.
            </h2>
            <p className="mt-4 text-white/55 text-lg leading-relaxed">
              We take security reports seriously. Email {SECURITY_EMAIL} with the details. We'll acknowledge within one business day.
            </p>
            <div className="mt-9 flex justify-center">
              <Button asChild size="lg" className="bg-[hsl(42,90%,50%)] hover:bg-[hsl(42,90%,45%)] text-[hsl(220,65%,12%)] font-bold h-12 px-10 text-base shadow-lg border-0">
                <a href={SECURITY_MAILTO}>
                  {SECURITY_EMAIL}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* FOOTER — same as landing */}
      <footer className="bg-[hsl(220,65%,7%)] border-t border-white/8 pt-14 pb-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo-dark.svg" alt="Zindeal" className="w-7 h-7 rounded-full" />
                <span className="font-serif font-semibold text-white text-base">Zindeal</span>
              </div>
              <p className="text-white/35 text-xs leading-relaxed">
                The system of record for college coach contracts — from the first offer to the day they re-sign.
              </p>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-4">Product</div>
              <ul className="space-y-2.5">
                {[["Coach lifecycle", "/#workflow"], ["Questions", "/#faq"]].map(([label, href]) => (
                  <li key={label}>
                    <a href={href} className="text-white/45 hover:text-white/80 transition-colors text-sm">{label}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-4">Company</div>
              <ul className="space-y-2.5">
                <li><Link href="/sign-in" className="text-white/45 hover:text-white/80 transition-colors text-sm">Sign in</Link></li>
                <li><a href={salesInquiryHref("Zindeal enterprise inquiry")} className="text-white/45 hover:text-white/80 transition-colors text-sm">Contact Sales</a></li>
                <li><a href={salesInquiryHref("Zindeal support")} className="text-white/45 hover:text-white/80 transition-colors text-sm">Support</a></li>
                <li><a href={STATUS_PAGE_URL} target="_blank" rel="noopener noreferrer" className="text-white/45 hover:text-white/80 transition-colors text-sm">System Status</a></li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-4">Legal</div>
              <ul className="space-y-2.5">
                {[["Terms of Service", "/terms"], ["Privacy Policy", "/privacy"], ["Subprocessors", "/subprocessors"], ["Security", "/security"]].map(([label, href]) => (
                  <li key={label}>
                    <Link href={href} className="text-white/45 hover:text-white/80 transition-colors text-sm">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="pt-6 border-t border-white/8 flex flex-col md:flex-row items-center justify-between gap-3">
            <span className="text-white/25 text-xs">© {new Date().getFullYear()} Zindeal. All rights reserved.</span>
            <span className="text-white/20 text-xs">Built for Division I, II, and III programs</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
